<template>
  <div 
    class="toast-notification"
    :class="[`toast-notification--${type}`, { 'toast-notification--persistent': persistent }]"
    @click="handleDismiss"
    @mouseenter="showTooltip = true"
    @mouseleave="showTooltip = false"
  >
    <div class="toast-notification__content">
      <span class="toast-notification__icon" v-if="icon">
        <font-awesome-icon :icon="['fal', icon]" />
      </span>
      <span class="toast-notification__message">{{ message }}</span>
      <button
        v-if="action"
        class="toast-notification__action"
        @click.stop="action.handler()"
      >
        {{ action.label }}
      </button>
    </div>

    <!-- Click to dismiss tooltip (only when no action button) -->
    <Transition name="tooltip">
      <div v-if="showTooltip && !action" class="toast-notification__tooltip">
        Click to dismiss
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'error', 'danger', 'warning', 'info', 'primary'].includes(value)
  },
  persistent: {
    type: Boolean,
    default: false
  },
  action: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['dismiss'])

const showTooltip = ref(false)

// Map types to icons
const icon = computed(() => {
  const iconMap = {
    success: 'check-circle',
    error: 'times-circle',
    danger: 'times-circle',
    warning: 'exclamation-triangle',
    info: 'info-circle',
    primary: 'info-circle'
  }
  return iconMap[props.type] || null
})

const handleDismiss = () => {
  emit('dismiss')
}
</script>

<style lang="scss" scoped>
.toast-notification {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 250px;
  max-width: 500px;
  margin-bottom: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: visible;
  
  // Base styles
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
  }

  &__icon {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
  }

  &__message {
    flex: 1;
    font-size: 1rem;
    line-height: 1.5;
    font-weight: 500;
  }

  &__action {
    flex-shrink: 0;
    margin-left: 0.5rem;
    padding: 0.25rem 0.75rem;
    border: 1px solid currentColor;
    border-radius: 4px;
    background: transparent;
    color: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.08);
    }
  }

  // Type variants - matching button styles
  &--success {
    background-color: hsl(158, 80%, 93.5%);
    color: hsl(158, 80%, 35%);
    border-color: hsl(158, 80%, 35%);
  }

  &--danger,
  &--error {
    background-color: hsl(348, 86%, 94.9%);
    color: hsl(348, 86%, 61%);
    border-color: hsl(348, 86%, 61%);
  }

  &--warning {
    background-color: hsl(34, 100%, 94.4%);
    color: hsl(34, 100%, 56%);
    border-color: hsl(34, 100%, 56%);
  }

  &--info {
    background-color: hsl(228.5, 88.4%, 95.61%);
    color: hsl(228.5, 88.4%, 62.9%);
    border-color: hsl(228.5, 88.4%, 62.9%);
  }

  &--primary {
    background-color: hsl(198, 100%, 95%);
    color: hsl(198, 100%, 50%);
    border-color: hsl(198, 100%, 50%);
  }

  // Persistent variant
  &--persistent {
    font-weight: 600;
  }

  // Tooltip
  &__tooltip {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 0.375rem 0.75rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    pointer-events: none;
    z-index: 10000;
  }
}

// Tooltip animation
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}
</style>